import { useState, useEffect } from 'react';
import { ArrowLeft, Loader2, Bell, BellOff, Timer, Coffee, Calendar, AlertCircle, Smartphone, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useProfile } from '@/hooks/useProfile';
import { useNotifications } from '@/hooks/useNotifications';
import { isNativePushSupported, openAppNotificationSettings } from '@/lib/pushNotifications';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { liveActivityService } from '@/services/liveActivityService';
import { Capacitor } from '@capacitor/core';

interface NotificationsSectionProps {
  onBack: () => void;
}

export function NotificationsSection({ onBack }: NotificationsSectionProps) {
  const { profile, updateProfile } = useProfile();
  const { permission, isSupported, isEnabled, isLoading, requestPermission } = useNotifications();
  const { toast } = useToast();
  
  const [timerComplete, setTimerComplete] = useState(profile?.notification_timer_complete ?? true);
  const [breakOver, setBreakOver] = useState(profile?.notification_break_over ?? true);
  const [taskDue, setTaskDue] = useState(profile?.notification_task_due ?? true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isTestingLiveActivity, setIsTestingLiveActivity] = useState(false);

  useEffect(() => {
    if (profile) {
      setTimerComplete(profile.notification_timer_complete ?? true);
      setBreakOver(profile.notification_break_over ?? true);
      setTaskDue(profile.notification_task_due ?? true);
    }
  }, [profile]);

  useEffect(() => {
    if (profile) {
      setHasChanges(
        timerComplete !== (profile.notification_timer_complete ?? true) ||
        breakOver !== (profile.notification_break_over ?? true) ||
        taskDue !== (profile.notification_task_due ?? true)
      );
    }
  }, [timerComplete, breakOver, taskDue, profile]);

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    try {
      const granted = await requestPermission();
      if (granted) {
        toast({
          title: 'Notifications enabled',
          description: 'You will now receive push notifications.',
        });
      } else {
        toast({
          title: 'Notifications blocked',
          description: isNativePushSupported()
            ? 'Go to Settings → Luma → Notifications to enable.'
            : 'Please enable notifications in your browser settings.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to request notification permission.',
        variant: 'destructive',
      });
    } finally {
      setIsRequesting(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await updateProfile({
        notification_timer_complete: timerComplete,
        notification_break_over: breakOver,
        notification_task_due: taskDue,
      });
      
      if (error) throw error;

      toast({
        title: 'Preferences saved',
        description: 'Your notification settings have been updated.',
      });
      setHasChanges(false);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to save preferences. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const renderPermissionStatus = () => {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 mb-6 p-3 rounded-lg border text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Checking notification permissions…</span>
        </div>
      );
    }

    if (!isSupported) {
      return (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Push notifications are not supported in this browser.
          </AlertDescription>
        </Alert>
      );
    }

    if (permission === 'denied') {
      return (
        <div className="mb-6 space-y-3">
          <Alert variant="destructive">
            <BellOff className="h-4 w-4" />
            <AlertDescription>
              {isNativePushSupported()
                ? 'Notifications are blocked. Open Settings to enable them for Luma.'
                : 'Notifications are blocked. To enable them, click the lock icon in your browser\'s address bar and allow notifications.'}
            </AlertDescription>
          </Alert>
          {isNativePushSupported() && (
            <Button
              onClick={async () => {
                const opened = await openAppNotificationSettings();
                if (!opened) {
                  toast({
                    title: 'Could not open Settings',
                    description: 'Go to Settings → Luma → Notifications to enable notifications.',
                    variant: 'destructive',
                  });
                }
              }}
              variant="outline"
              className="w-full"
              size="lg"
            >
              <Settings className="h-4 w-4 mr-2" />
              Open Settings
            </Button>
          )}
        </div>
      );
    }

    if (permission === 'default') {
      return (
        <Button
          onClick={handleRequestPermission}
          disabled={isRequesting}
          className="w-full mb-6"
          size="lg"
        >
          {isRequesting ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Bell className="h-4 w-4 mr-2" />
          )}
          Enable Push Notifications
        </Button>
      );
    }

    return (
      <div className="flex items-center gap-2 mb-6 p-3 rounded-lg bg-primary/10 text-primary">
        <Bell className="h-4 w-4" />
        <span className="text-sm font-medium">Notifications enabled</span>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="text-lg font-semibold">Notifications</h2>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {renderPermissionStatus()}

        {/* Notification Types */}
        <div className="space-y-6">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-medium">Notification Types</h3>
          </div>

          <p className="text-sm text-muted-foreground -mt-2">
            Choose which notifications you want to receive.
          </p>

          <div className="space-y-4">
            {/* Timer Complete */}
            <div className={cn(
              "flex items-center justify-between p-4 rounded-lg border transition-opacity",
              !isEnabled && "opacity-50"
            )}>
              <div className="flex items-start gap-3">
                <Timer className="h-5 w-5 text-primary mt-0.5" />
                <div className="space-y-0.5">
                  <Label htmlFor="timer-complete" className="font-medium">Timer Complete</Label>
                  <p className="text-sm text-muted-foreground">
                    Get notified when your focus session ends
                  </p>
                </div>
              </div>
              <Switch
                id="timer-complete"
                checked={timerComplete}
                onCheckedChange={setTimerComplete}
                disabled={!isEnabled}
              />
            </div>

            {/* Break Over */}
            <div className={cn(
              "flex items-center justify-between p-4 rounded-lg border transition-opacity",
              !isEnabled && "opacity-50"
            )}>
              <div className="flex items-start gap-3">
                <Coffee className="h-5 w-5 text-primary mt-0.5" />
                <div className="space-y-0.5">
                  <Label htmlFor="break-over" className="font-medium">Break Over</Label>
                  <p className="text-sm text-muted-foreground">
                    Get reminded when your break is finished
                  </p>
                </div>
              </div>
              <Switch
                id="break-over"
                checked={breakOver}
                onCheckedChange={setBreakOver}
                disabled={!isEnabled}
              />
            </div>

            {/* Task Due */}
            <div className={cn(
              "flex items-center justify-between p-4 rounded-lg border transition-opacity",
              !isEnabled && "opacity-50"
            )}>
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-primary mt-0.5" />
                <div className="space-y-0.5">
                  <Label htmlFor="task-due" className="font-medium">Task Reminders</Label>
                  <p className="text-sm text-muted-foreground">
                    Get reminded 30 minutes before tasks are due
                  </p>
                </div>
              </div>
              <Switch
                id="task-due"
                checked={taskDue}
                onCheckedChange={setTaskDue}
                disabled={!isEnabled}
              />
            </div>
          </div>
        </div>

        {/* Live Activity Test - iOS only */}
        {Capacitor.getPlatform() === 'ios' && (
          <div className="space-y-2 pt-4 border-t">
            <div className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-muted-foreground" />
              <h3 className="font-medium">Live Activity</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Test the lock screen timer. Lock your phone to see it on the lock screen or Dynamic Island.
            </p>
            <Button
              variant="outline"
              size="sm"
              disabled={isTestingLiveActivity}
              onClick={async () => {
                setIsTestingLiveActivity(true);
                try {
                  const success = await liveActivityService.startTimer({
                    taskName: 'Test Session',
                    sessionDuration: 1500,
                    timeToNextMilestone: 300,
                    currentMilestone: 0,
                    totalMilestones: 5,
                    motivation: 'Testing Live Activity! 🚀',
                    timerMode: 'maker',
                  });
                  if (success) {
                    toast({
                      title: 'Live Activity started',
                      description: 'Lock your phone to see it on the lock screen.',
                    });
                  } else {
                    toast({
                      title: 'Live Activity failed',
                      description: 'Check console for errors. Requires iOS 16.1+ and enabled Live Activities.',
                      variant: 'destructive',
                    });
                  }
                } finally {
                  setIsTestingLiveActivity(false);
                }
              }}
            >
              {isTestingLiveActivity ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Smartphone className="h-4 w-4 mr-2" />
              )}
              Test Live Activity
            </Button>
          </div>
        )}
      </div>

      {/* Footer */}
      {hasChanges && isEnabled && (
        <div className="p-4 border-t">
          <Button
            onClick={handleSave}
            className="w-full"
            disabled={isSaving}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Save Preferences'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
